import { useCallback, useEffect, useState } from "react"

import type { StatRow, UserChallengeRow, UserChallengesResponse } from "./api"

export interface CacheDiff {
  message: string
  timestamp: number
}

interface CachedHistory {
  data: UserChallengeRow[]
  timestamp: number
}

interface CachedStats {
  data: StatRow[]
  timestamp: number
}

const STATS_KEY = "sortaballs-leaderboard-cache"
const HISTORY_KEY = "sortaballs-history-cache"

const loadStats = (): CachedStats | null=> {
  try {
    const raw = localStorage.getItem(STATS_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

const saveStats = (data: StatRow[]): void=> {
  localStorage.setItem(STATS_KEY, JSON.stringify({ data, timestamp: Date.now() }))
}

const loadHistory = (): CachedHistory | null=> {
  try {
    const raw = localStorage.getItem(HISTORY_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

const saveHistory = (data: UserChallengeRow[]): void=> {
  localStorage.setItem(HISTORY_KEY, JSON.stringify({ data, timestamp: Date.now() }))
}

const formatTimeAgo = (timestamp: number): string=> {
  const diff = Date.now() - timestamp
  const minutes = Math.floor(diff / 60000)
  if (minutes < 1) return "just now"
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

export function useCachedHistoryQuery(loader: ()=> Promise<UserChallengesResponse>) {
  const [data, setData] = useState<null | UserChallengeRow[]>(null)
  const [diff, setDiff] = useState<CacheDiff | null>(null)
  const [failed, setFailed] = useState(false)
  const [serverTime, setServerTime] = useState<Date | null>(null)

  useEffect(()=> {
    let alive = true
    loader()
      .then(result=> {
        if (!alive) return
        setServerTime(new Date(result.server_time))
        const prev = loadHistory()
        saveHistory(result.challenges)
        if (prev) {
          const prevIds = new Set(prev.data.map(r=> r.id))
          const newGames = result.challenges.filter(r=> !prevIds.has(r.id))
          const newWins = newGames.filter(r=> r.status === "won").length
          const newLosses = newGames.filter(r=> r.status === "lost").length
          if (newGames.length > 0) {
            const parts: string[] = []
            if (newWins > 0) parts.push(`won ${newWins}`)
            if (newLosses > 0) parts.push(`lost ${newLosses}`)
            if (parts.length === 0) parts.push(`${newGames.length} new`)
            setDiff({
              message: `Since ${formatTimeAgo(prev.timestamp)}: ${parts.join(", ")}`,
              timestamp: prev.timestamp,
            })
          }
        }
        setData(result.challenges)
      })
      .catch(()=> {
        if (alive) setFailed(true)
      })
    return ()=> {
      alive = false
    }
  }, [loader])
  return { data, diff, failed, serverTime }
}

export function useCachedStatsQuery(loader: ()=> Promise<StatRow[]>) {
  const [data, setData] = useState<null | StatRow[]>(null)
  const [diff, setDiff] = useState<CacheDiff | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(()=> {
    let alive = true
    loader()
      .then(result=> {
        if (!alive) return
        const prev = loadStats()
        saveStats(result)
        if (prev) {
          const prevMap = new Map(prev.data.map(r=> [r.id, r]))
          let winsDelta = 0
          let lossesDelta = 0
          for (const row of result) {
            const old = prevMap.get(row.id)
            if (old) {
              winsDelta += row.wins - old.wins
              lossesDelta += row.losses - old.losses
            } else {
              winsDelta += row.wins
              lossesDelta += row.losses
            }
          }
          if (winsDelta !== 0 || lossesDelta !== 0) {
            const parts: string[] = []
            if (winsDelta > 0) parts.push(`won ${winsDelta}`)
            if (lossesDelta > 0) parts.push(`lost ${lossesDelta}`)
            setDiff({
              message: `Since ${formatTimeAgo(prev.timestamp)}: ${parts.join(", ")}`,
              timestamp: prev.timestamp,
            })
          }
        }
        setData(result)
      })
      .catch(()=> {
        if (alive) setFailed(true)
      })
    return ()=> {
      alive = false
    }
  }, [loader])
  return { data, diff, failed }
}
